import { WishlistButtonProps } from "@omni/types/components/props/wishlistButton.props";
import { BaseStore } from "@omni/types/enums/baseStore.enum";
import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { PageTemplate } from "@omni/types/enums/pageTemplate.enum";
import { ReactNode, useEffect } from "react";
import { useSelector } from "react-redux";

import { useWishlistService } from "@/api/hooks/wishlistService.hook";
import { WishlistButtonContextProvider } from "@/components/Navigation/Buttons/WishlistButton/context/wishlistButton.context";
import { useFeatureToggle } from "@/context/featureToggles.context";
import { useThunkDispatch } from "@/redux/dispatcher";
import { getCustomer } from "@/redux/modules/api/data/customer/customer.selectors";
import * as WishlistModule from "@/redux/modules/api/data/wishlist/wishlist.module";
import * as WishlistSelectors from "@/redux/modules/api/data/wishlist/wishlist.selectors";
import CallService, { CallServiceKey } from "@/services/call.service";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";
import { isPageTemplate } from "@/utils/pageTemplate.utils";

export type Props = {
    children: ReactNode;
} & WishlistButtonProps<BaseStore>;

export const Base = ({
    icon,
    text,
    ariaLabel,
    hover,
    invert,
    link,
    invertMobile,
    textHidden,
    children
}: Props) => {
    const isClientSide = useIsClientSide();
    const wishlists = useSelector(WishlistSelectors.getWishlists);
    const dispatch = useThunkDispatch();
    const customer = useSelector(getCustomer);
    const isLoggedIn = customer && !customer.guest;

    const wishlistBadgeEnabled = useFeatureToggle(
        FeatureToggleKey.FEATURE_WISHLIST_BADGE
    );

    const [{ data }, loadWishlist] = useWishlistService(
        {
            url: "/products/count",
            method: "GET"
        },
        {
            manual: true
        }
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (isClientSide) {
            if (wishlistBadgeEnabled) {
                loadWishlist().then(
                    (payload) => {
                        if (payload.data > 0) {
                            CallService.enable(CallServiceKey.Wishlist);
                        } else {
                            CallService.disable(CallServiceKey.Wishlist);
                        }
                    },
                    () => {
                        CallService.enable(CallServiceKey.Wishlist);
                    }
                );
            } else {
                dispatch(WishlistModule.loadAll());
            }
        }
    }, [isClientSide]);

    const productCount = wishlists.reduce(
        (acc, wishlist) => acc + wishlist?.products?.length || 0,
        0
    );
    const itemCount = isClientSide ? (productCount ? productCount : data) : 0;

    const showMessages = !(
        isPageTemplate(PageTemplate.ShoppingCart) && isLoggedIn
    );

    const alerts = itemCount
        ? itemCount < 100
            ? itemCount
            : "+99"
        : undefined;

    return (
        <WishlistButtonContextProvider
            headerButtonProps={{
                icon: icon,
                alerts: alerts,
                invert: invert,
                link: link,
                text: text,
                ariaLabel: ariaLabel,
                hover: hover,
                invertMobile: invertMobile,
                textHidden: textHidden
            }}
            showMessages={showMessages}
        >
            <div className={"tw:mx-1"}>{children}</div>
        </WishlistButtonContextProvider>
    );
};
